/**
 * ChecklistEditor - Full-page form for creating or editing a checklist
 * template. Renders as a page swap (not a modal) because the dynamic
 * items list and multi-select pivots benefit from the extra real estate.
 *
 * Saving behavior (server-side):
 *   - new      → POST /checklists                — creates a v1 row
 *   - existing → PATCH /checklists/{id}          — server creates a NEW
 *                version (parent_checklist_id chain), inactivates the
 *                prior version, and returns the new template. The old
 *                version remains queryable but is hidden from the
 *                assignment dropdown.
 *
 * Visibility model (matches backend Checklist::scopeVisibleTo):
 *   - is_visible_to_all=true → all authenticated users see it
 *   - companyIds            → users in any of those companies see it
 *   - userIds               → those individual users see it
 *   Super Admins always see every template regardless of pivot rows.
 *
 * Auto-growing textareas: details and additional_details textareas
 * resize to fit their content via an onInput handler that sets the
 * element's height to its scrollHeight.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, AutoGrowTextarea, Alert, Checkbox } from '../../components/atoms';
import { VisibilityFieldset } from '../../components/molecules';
import { api, camelizeKeys } from '../../services/api';
import {
    checklistTemplateService,
    ChecklistTemplate,
    ChecklistTemplateItemPayload,
    ChecklistTemplatePayload,
    ChecklistTemplateType,
} from '../../services/checklistTemplateService';
import { qk } from '../../lib/queryKeys';
import type { ChecklistEditTarget } from './CMSPage';
import { PageContainer } from '../../components/templates/PageContainer';

interface ChecklistEditorProps {
    /** { mode: 'new', type } for a fresh template, { id } to edit an existing one */
    target: Exclude<ChecklistEditTarget, null>;
    /** Called to return to the list view without saving (Cancel / back). */
    onClose: () => void;
    /**
     * Called after a successful save with the kind the template was saved
     * as, so the list can open on the sub-tab where it now lives — the type
     * picker is in here, so it can differ from the sub-tab the editor was
     * opened from. Falls back to onClose when not given.
     */
    onSaved?: (type: ChecklistTemplateType) => void;
}

/**
 * Local item shape. Differs from the server payload only in that it
 * carries a temp-id (clientKey) so React can key the row across edits.
 */
interface EditorItem extends ChecklistTemplateItemPayload {
    /** React key — generated client-side, not sent to the server */
    clientKey: string;
}

/** Small types for the visibility multi-selects */
interface CompanyOption { id: number; name: string }
interface UserOption    { id: number; name: string; email: string }

const newItem = (): EditorItem => ({
    clientKey: Math.random().toString(36).slice(2),
    title: '',
    details: '',
    additionalDetails: '',
    // photo_mode + photo_required replace the legacy require_photo
    // boolean. Default is "no camera UI" — managers opt in explicitly.
    photoMode: 'none',
    photoRequired: false,
    isRequired: true,
    sortOrder: 0,
});

const ChecklistEditor: React.FC<ChecklistEditorProps> = ({ target, onClose, onSaved }) => {
    /* ---------------------------------------------------------------
     * Form state
     * --------------------------------------------------------------- */
    /* A target carrying an id is an edit; anything else is the create mode,
     * which brings the kind the author clicked "New" under with it. */
    const isEditing = 'id' in target;
    const editingId = isEditing ? target.id : null;
    const queryClient = useQueryClient();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    /* Template type — drives JobForm dropdown filtering and which
     * field-side renderer (binary checkbox vs Good / Needs Attention /
     * Critical rating) is used. A new template starts on the kind whose sub-tab the
     * author pressed "New" from; an edit overwrites this from the server
     * once the template loads. The radio stays editable either way. */
    const [type, setType] = useState<ChecklistTemplateType>(
        isEditing ? 'work_order' : target.type,
    );
    const [isVisibleToAll, setIsVisibleToAll] = useState(false);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<number>>(new Set());
    const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
    const [items, setItems] = useState<EditorItem[]>([newItem()]);

    /* CMS-managed "default for" associations. The checklist can be the
     * default for either a whole business line (when no sub-types are
     * selected) OR for one-or-more sub-types under a single line. The
     * Set keeps toggle state cheap; serialized to an array on save. */
    const [defaultBusinessLineId, setDefaultBusinessLineId] = useState<number | null>(null);
    const [defaultSubTypeIds, setDefaultSubTypeIds] = useState<Set<number>>(new Set());
    /* Loaded once on mount — drives the line dropdown + sub-type multiselect. */
    const [businessLines, setBusinessLines] = useState<import('../../services/businessLineService').BusinessLine[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* ---------------------------------------------------------------
     * Lookups (companies + users + business lines) — always fetched.
     * Pull a generous page so all options are visible (999 mirrors the
     * AdminPage pattern for one-shot lookups). Responses arrive snake_case;
     * api.ts does NOT camelize, so camelizeKeys runs inside the queryFn
     * before reading camelCase fields, and the dedupe-by-id lives here too.
     * --------------------------------------------------------------- */
    const lookupsQuery = useQuery({
        queryKey: ['checklistEditorLookups'] as const,
        queryFn: async () => {
            const [companiesRes, usersRes, linesRes] = await Promise.all([
                api.get<any>('/companies', { per_page: '999' }),
                api.get<any>('/users', { per_page: '999' }),
                api.get<any>('/business-lines'),
            ]);
            const rawCompanyRows = Array.isArray(companiesRes.data) ? companiesRes.data : (companiesRes.data?.data ?? []);
            const rawUserRows = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.data ?? []);
            const companyRows = camelizeKeys(rawCompanyRows) as Record<string, unknown>[];
            const userRows = camelizeKeys(rawUserRows) as Record<string, unknown>[];

            /* Dedupe by id before storing.
             * The /companies endpoint can return the same company multiple
             * times when it joins through company_users, and React rendering
             * the same id key twice causes a subtle bug: the
             * selectedCompanyIds Set lookup returns the same boolean for
             * every duplicate row, so toggling one checkbox visually
             * toggles all of them. Deduping at ingestion eliminates the
             * symptom without leaning on the API's query shape. */
            const companyMap = new Map<number, CompanyOption>();
            for (const c of companyRows as any[]) {
                const id = Number(c.companyId ?? c.id);
                if (!Number.isFinite(id) || companyMap.has(id)) continue;
                companyMap.set(id, { id, name: c.name });
            }

            /* Same defensive dedupe for users — users typically don't
             * duplicate but the cost is negligible and protects against
             * future endpoint changes. Sorted A-Z by display name so the
             * visibility list is predictable for editors. */
            const userMap = new Map<number, UserOption>();
            for (const u of userRows as any[]) {
                const id = Number(u.id);
                if (!Number.isFinite(id) || userMap.has(id)) continue;
                userMap.set(id, {
                    id,
                    name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email,
                    email: u.email,
                });
            }

            /* Business Lines tree — camelized so sub_types becomes
               subTypes etc. for the picker below. */
            const rawLineRows = (linesRes.data as any)?.data ?? [];

            return {
                companies: Array.from(companyMap.values()),
                users: Array.from(userMap.values()).sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                ),
                businessLines: camelizeKeys(rawLineRows) as import('../../services/businessLineService').BusinessLine[],
            };
        },
    });

    /* Existing template — only fetched when editing. */
    const templateQuery = useQuery({
        queryKey: qk.checklistTemplates.detail(editingId ?? ''),
        enabled: isEditing && !!editingId,
        queryFn: async () => {
            const res = await checklistTemplateService.getById(editingId as string);
            /* Server returns { data: ChecklistTemplate } in snake_case;
             * api.ts does NOT camelize, so do it locally before reading. */
            const rawTpl = (res.data as any).data;
            return camelizeKeys(rawTpl) as ChecklistTemplate;
        },
    });

    /* Lookup options for the visibility multi-selects, derived from the query. */
    const companies: CompanyOption[] = useMemo(() => lookupsQuery.data?.companies ?? [], [lookupsQuery.data]);
    const users: UserOption[] = useMemo(() => lookupsQuery.data?.users ?? [], [lookupsQuery.data]);

    /* Keep the businessLines form-state mirror in sync with the lookups query
     * (the "Default for" sub-type picker reads from this state). */
    useEffect(() => {
        if (lookupsQuery.data) {
            setBusinessLines(lookupsQuery.data.businessLines as any);
        }
    }, [lookupsQuery.data]);

    /* Hydrate the form from the fetched template once it arrives. Mirrors the
     * old imperative population that ran inside the load effect. */
    useEffect(() => {
        const tpl: ChecklistTemplate | undefined = templateQuery.data;
        if (!tpl) return;
        setTitle(tpl.title);
        setDescription(tpl.description ?? '');
        /* Backfill: pre-existing rows that were created before
         * the type column existed will come back as undefined.
         * Treat those as 'work_order' so the radio renders a
         * sensible default. */
        setType((tpl.type ?? 'work_order') as ChecklistTemplateType);
        setIsVisibleToAll(tpl.isVisibleToAll);
        setSelectedCompanyIds(new Set((tpl.companies ?? []).map((c) => Number(c.companyId))));
        setSelectedUserIds(new Set((tpl.users ?? []).map((u) => Number(u.id))));

        /* Hydrate the "Default for" controls. The relations
         * are eager-loaded on the show endpoint so we can
         * resolve both the line dropdown and the sub-type
         * multiselect without a second round-trip. When the
         * checklist is currently the default for multiple
         * sub-types they should all share the same parent
         * line — pull the line from the first sub-type and
         * pre-select all of them. */
        const lineRow = (tpl.defaultForBusinessLines ?? [])[0];
        const subRows = tpl.defaultForSubTypes ?? [];
        if (subRows.length > 0) {
            setDefaultBusinessLineId(Number(subRows[0].businessLineId));
            setDefaultSubTypeIds(new Set(subRows.map((s) => Number(s.id))));
        } else if (lineRow) {
            setDefaultBusinessLineId(Number(lineRow.id));
            setDefaultSubTypeIds(new Set());
        }
        setItems(
            (tpl.items ?? [])
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((it) => ({
                    clientKey: String(it.id),
                    title: it.title,
                    details: it.details ?? '',
                    additionalDetails: it.additionalDetails ?? '',
                    photoMode: it.photoMode ?? 'none',
                    photoRequired: !!it.photoRequired,
                    isRequired: it.isRequired,
                    sortOrder: it.sortOrder,
                }))
        );
    }, [templateQuery.data]);

    /* Combined load + error state. The page shows the loading view until both
     * the lookups and (when editing) the template have resolved. */
    const isLoading = lookupsQuery.isLoading || (isEditing && templateQuery.isLoading);
    const loadError = lookupsQuery.error || templateQuery.error;
    useEffect(() => {
        if (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Failed to load');
        }
    }, [loadError]);

    /* ---------------------------------------------------------------
     * Item list handlers
     * --------------------------------------------------------------- */
    const updateItem = (clientKey: string, patch: Partial<EditorItem>) => {
        setItems((prev) => prev.map((it) => (it.clientKey === clientKey ? { ...it, ...patch } : it)));
    };
    const addItem = () => setItems((prev) => [...prev, newItem()]);
    const removeItem = (clientKey: string) =>
        setItems((prev) => prev.filter((it) => it.clientKey !== clientKey));
    const moveItem = (clientKey: string, direction: -1 | 1) => {
        setItems((prev) => {
            const idx = prev.findIndex((it) => it.clientKey === clientKey);
            const target = idx + direction;
            if (idx < 0 || target < 0 || target >= prev.length) return prev;
            const copy = [...prev];
            [copy[idx], copy[target]] = [copy[target], copy[idx]];
            return copy;
        });
    };

    /* Adapt the local UserOption (id/name/email) to the molecule's
     * generic VisibilityOption (id/name/secondary). Companies already
     * match the shape, so they pass through unchanged. Memoized so
     * the molecule's internal dedupe useMemo doesn't churn. */
    const userVisibilityOptions = useMemo(
        () => users.map((u) => ({ id: u.id, name: u.name, secondary: u.email })),
        [users]
    );

    /* ---------------------------------------------------------------
     * Save
     *
     * Create POSTs a v1; update PATCHes (the server forks a new version).
     * Either way we invalidate the checklistTemplates cache so the list tab
     * reflects the change, plus businessLines since the "Default for"
     * associations can reassign which line/sub-type a template defaults to.
     * --------------------------------------------------------------- */
    const saveMutation = useMutation({
        mutationFn: (vars: { editingId: string | null; payload: ChecklistTemplatePayload }) =>
            vars.editingId
                ? checklistTemplateService.update(vars.editingId, vars.payload)
                : checklistTemplateService.create(vars.payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qk.checklistTemplates.all });
            queryClient.invalidateQueries({ queryKey: qk.businessLines.all });
        },
    });

    const handleSave = async () => {
        // Trim & validate before sending — title + at least one item with a title
        const cleanItems = items
            .map((it, idx) => ({
                title: it.title.trim(),
                details: it.details?.toString().trim() || null,
                additionalDetails: it.additionalDetails?.toString().trim() || null,
                // photo_required only makes sense when there's actually
                // a photo to attach, so when mode='none' we force the
                // required flag off to keep the row internally consistent.
                photoMode: it.photoMode ?? 'none',
                photoRequired: (it.photoMode ?? 'none') !== 'none' && !!it.photoRequired,
                isRequired: !!it.isRequired,
                sortOrder: idx, // re-index from current order
            }))
            .filter((it) => it.title.length > 0);

        if (!title.trim()) {
            setError('Title is required.');
            return;
        }
        if (cleanItems.length === 0) {
            setError('Add at least one item with a title.');
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            const payload = {
                title: title.trim(),
                description: description.trim() || null,
                type,
                isVisibleToAll,
                companyIds: Array.from(selectedCompanyIds),
                userIds: Array.from(selectedUserIds),
                items: cleanItems,
                /* "Default for" associations — send both keys explicitly
                   so the backend's syncDefaults pass can clear stale
                   claims even when ops removes a previously-set default. */
                defaultBusinessLineId: defaultBusinessLineId,
                defaultSubTypeIds: Array.from(defaultSubTypeIds),
            };

            await saveMutation.mutateAsync({ editingId: isEditing ? editingId : null, payload });
            if (onSaved) onSaved(type);
            else onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    /* ---------------------------------------------------------------
     * Render
     * --------------------------------------------------------------- */
    if (isLoading) {
        return (
            <div className="max-w-[1024px] mx-auto px-16px py-32px text-center text-brand-text-muted">
                Loading...
            </div>
        );
    }

    return (
        <PageContainer block="checklist-editor" dataRef="page-checklist-editor" width="content">
            {/* Header */}
            <div className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between mb-24px gap-12px">
                <div className="min-w-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center min-h-44px text-14px text-brand-text-muted hover:text-brand-primary mb-4px"
                    >
                        ← Back to checklists
                    </button>
                    <h1 className="text-24px tablet:text-28px font-semibold text-brand-text">
                        {/* Name the kind while creating so the heading confirms
                            which sub-tab the author came from. On an edit the
                            type only settles once the template loads, so the
                            heading stays neutral rather than flickering. */}
                        {isEditing
                            ? 'Edit Checklist'
                            : type === 'inspection' ? 'New Inspection Template' : 'New Work Order Template'}
                    </h1>
                    {isEditing && (
                        <p className="text-12px text-brand-text-muted mt-4px">
                            Saving creates a new version. Existing assignments keep their snapshot.
                        </p>
                    )}
                </div>
                <div className="checklist-editor__header-actions flex flex-wrap items-center gap-8px w-full tablet:w-auto justify-end">
                    <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} isLoading={isSaving}>
                        {isEditing ? 'Save New Version' : 'Create Checklist'}
                    </Button>
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <Alert variant="error" className="mb-16px">{error}</Alert>
            )}

            {/* Title + description card */}
            <section className="bg-white border border-brand-border rounded-md p-16px tablet:p-20px mb-16px">
                <h2 className="text-16px font-semibold text-brand-text mb-12px">Details</h2>
                <div className="mb-12px">
                    <label className="block text-12px font-medium text-brand-text mb-4px">Title *</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g., Standard Janitorial Daily Close"
                        className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                </div>

                {/* Template type — Work Order vs Inspection.
                 * Field-side renderers are different (binary checkbox vs
                 * Good / Needs Attention / Critical rating). The CMS UI for
                 * authoring items remains the same regardless of type,
                 * so the only signal is this radio. */}
                <div className="mb-12px">
                    <label className="block text-12px font-medium text-brand-text mb-4px">Type *</label>
                    <div className="flex flex-wrap gap-8px">
                        <label className={['flex-1 min-w-[200px] flex items-start gap-8px px-12px py-8px border rounded-md cursor-pointer transition-colors', type === 'work_order' ? 'border-brand-primary bg-brand-primary/5' : 'border-brand-border bg-white hover:border-brand-primary/50'].join(' ')}>
                            <input type="radio" name="checklist-type" value="work_order" checked={type === 'work_order'} onChange={() => setType('work_order')} className="mt-2px w-14px h-14px text-brand-primary" />
                            <span className="flex-1">
                                <span className="block text-13px font-medium text-brand-text">Work Order</span>
                                <span className="block text-11px text-brand-text-muted leading-snug">Each item is a binary check-off. Used in the work-order completion workflow.</span>
                            </span>
                        </label>
                        <label className={['flex-1 min-w-[200px] flex items-start gap-8px px-12px py-8px border rounded-md cursor-pointer transition-colors', type === 'inspection' ? 'border-brand-primary bg-brand-primary/5' : 'border-brand-border bg-white hover:border-brand-primary/50'].join(' ')}>
                            <input type="radio" name="checklist-type" value="inspection" checked={type === 'inspection'} onChange={() => setType('inspection')} className="mt-2px w-14px h-14px text-brand-primary" />
                            <span className="flex-1">
                                <span className="block text-13px font-medium text-brand-text">Inspection</span>
                                <span className="block text-11px text-brand-text-muted leading-snug">Each item is rated Good / Needs Attention / Critical. Critical requires a note.</span>
                            </span>
                        </label>
                    </div>
                </div>

                <div>
                    <label className="block text-12px font-medium text-brand-text mb-4px">Description</label>
                    <AutoGrowTextarea
                        value={description}
                        onChange={setDescription}
                        placeholder="Optional notes for the CMS — not shown to field users"
                        minRows={2}
                    />
                </div>
            </section>

            {/* Default-for card — line dropdown + sub-type multiselect.
                When the user picks a line WITHOUT any sub-types, the
                checklist becomes the line-level default. Picking
                sub-types claims them individually and clears the
                line-level slot. Each (line, sub-type) can have at most
                one default checklist; the server's syncDefaults pass
                handles the swap atomically. */}
            <section className="bg-white border border-brand-border rounded-md p-16px tablet:p-20px mb-16px">
                <h2 className="text-16px font-semibold text-brand-text mb-12px">Default For</h2>
                <p className="text-12px text-brand-text-muted mb-12px">
                    Auto-attach this checklist to new {type === 'inspection' ? 'inspections' : 'work orders'} that match the selected Business Line + Sub Type. Leave the line blank to keep it as a manually-assigned template.
                </p>
                <div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
                    <div>
                        <label className="block text-12px font-medium text-brand-text mb-4px">Business Line</label>
                        <select
                            value={defaultBusinessLineId ?? ''}
                            onChange={(e) => {
                                const v = e.target.value;
                                setDefaultBusinessLineId(v ? Number(v) : null);
                                /* Clear any sub-type selections — they're
                                   only valid under the previous line. */
                                setDefaultSubTypeIds(new Set());
                            }}
                            className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white"
                        >
                            <option value="">— None —</option>
                            {businessLines.filter((bl: any) => bl.isActive ?? bl.is_active).map((bl: any) => (
                                <option key={bl.id} value={bl.id}>{bl.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-12px font-medium text-brand-text mb-4px">Sub Types (multi-select)</label>
                        <div className="max-h-[160px] overflow-y-auto border border-brand-border rounded-md p-8px bg-white">
                            {(() => {
                                const line = (businessLines as any[]).find((bl) => Number(bl.id) === defaultBusinessLineId);
                                const subs = ((line?.subTypes ?? line?.sub_types) ?? []).filter((s: any) => s.isActive ?? s.is_active);
                                if (!line) return <p className="text-12px text-brand-text-muted">Select a Business Line first.</p>;
                                if (subs.length === 0) return <p className="text-12px text-brand-text-muted">This line has no sub-types. The default will apply line-wide.</p>;
                                /* A grid, not the bare box: Checkbox's root is an inline-flex
                                   <label>, so loose in a block they ran together like words,
                                   each label butting into the next box. Two columns keep five
                                   sub-types inside the 160px cap; no row gap, as the rows are
                                   already 44px on a phone and a gap tipped it into scrolling. */
                                return (
                                    <div className="checklist-editor__default-sub-types grid grid-cols-2 gap-x-16px">
                                        {subs.map((s: any) => {
                                            const id = Number(s.id);
                                            const checked = defaultSubTypeIds.has(id);
                                            return (
                                                <Checkbox
                                                    key={id}
                                                    checked={checked}
                                                    onChange={() => {
                                                        setDefaultSubTypeIds((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(id)) next.delete(id);
                                                            else next.add(id);
                                                            return next;
                                                        });
                                                    }}
                                                    label={s.label}
                                                    className="checklist-editor__default-sub-type items-center"
                                                    labelClassName="text-13px"
                                                    dataRef="page-checklist-editor-default-sub-type"
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </section>

            {/* Visibility card — reusable molecule. It owns the
             * dedupe-by-id, filter inputs, and disabled-dim treatment
             * so every record type with a visibility pivot stays
             * visually and behaviorally identical. */}
            <VisibilityFieldset
                className="mb-16px"
                companies={companies}
                users={userVisibilityOptions}
                isVisibleToAll={isVisibleToAll}
                onChangeIsVisibleToAll={setIsVisibleToAll}
                selectedCompanyIds={selectedCompanyIds}
                onChangeSelectedCompanyIds={setSelectedCompanyIds}
                selectedUserIds={selectedUserIds}
                onChangeSelectedUserIds={setSelectedUserIds}
            />

            {/* Items card — dynamic list */}
            <section className="bg-white border border-brand-border rounded-md p-16px tablet:p-20px mb-16px">
                <div className="flex items-center justify-between mb-12px">
                    <h2 className="text-16px font-semibold text-brand-text">Items ({items.length})</h2>
                    <Button variant="secondary" size="sm" onClick={addItem}>+ Add Item</Button>
                </div>

                <div className="space-y-12px">
                    {items.map((it, idx) => (
                        <div key={it.clientKey} className="border border-brand-border rounded-md p-12px bg-brand-surface/40">
                            <div className="flex items-start gap-8px mb-8px">
                                {/* Sort handle / item index */}
                                <div className="flex flex-col items-center gap-2px pt-4px">
                                    <button
                                        type="button"
                                        onClick={() => moveItem(it.clientKey, -1)}
                                        disabled={idx === 0}
                                        className="inline-flex items-center justify-center w-32px h-32px rounded-4px text-brand-text-muted hover:text-brand-primary hover:bg-brand-surface disabled:opacity-30 transition-colors"
                                        aria-label="Move up"
                                    >
                                        ▲
                                    </button>
                                    <span className="text-11px text-brand-text-muted">{idx + 1}</span>
                                    <button
                                        type="button"
                                        onClick={() => moveItem(it.clientKey, 1)}
                                        disabled={idx === items.length - 1}
                                        className="inline-flex items-center justify-center w-32px h-32px rounded-4px text-brand-text-muted hover:text-brand-primary hover:bg-brand-surface disabled:opacity-30 transition-colors"
                                        aria-label="Move down"
                                    >
                                        ▼
                                    </button>
                                </div>

                                {/* Item fields — main column */}
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        value={it.title}
                                        onChange={(e) => updateItem(it.clientKey, { title: e.target.value })}
                                        placeholder="Item title (required)"
                                        className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary mb-8px"
                                    />
                                    <AutoGrowTextarea
                                        value={(it.details ?? '') as string}
                                        onChange={(v) => updateItem(it.clientKey, { details: v })}
                                        placeholder="Details (auto-grow)"
                                        minRows={2}
                                    />
                                    <div className="h-8px" />
                                    <AutoGrowTextarea
                                        value={(it.additionalDetails ?? '') as string}
                                        onChange={(v) => updateItem(it.clientKey, { additionalDetails: v })}
                                        placeholder="Additional details / callout (optional)"
                                        minRows={2}
                                    />

                                    {/* Per-item flags. Photo controls are
                                     *  a two-axis pair: pick the *kind* of
                                     *  capture surface (none/photo/before+
                                     *  after) and toggle whether the chosen
                                     *  photo(s) gate completion. The "required"
                                     *  checkbox is hidden + auto-disabled when
                                     *  the picker is on 'none' so the form
                                     *  state can't end up with a nonsense
                                     *  "no photo but required" combination. */}
                                    <div className="flex flex-wrap items-center gap-16px mt-8px">
                                        <label className="flex items-center gap-6px">
                                            <span className="text-12px font-medium text-brand-text-muted uppercase tracking-wide">Photo</span>
                                            <select
                                                value={it.photoMode ?? 'none'}
                                                onChange={(e) => {
                                                    // When the user moves back to 'none',
                                                    // also force-off the required flag.
                                                    const nextMode = e.target.value as 'none' | 'photo' | 'before_after';
                                                    updateItem(it.clientKey, {
                                                        photoMode: nextMode,
                                                        photoRequired: nextMode === 'none' ? false : it.photoRequired,
                                                    });
                                                }}
                                                className="text-16px desktop:text-12px px-8px py-4px border border-brand-border rounded-md text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                            >
                                                <option value="none">No photo</option>
                                                <option value="photo">Photo</option>
                                                <option value="before_after">Before / After Photo</option>
                                            </select>
                                        </label>
                                        {it.photoMode && it.photoMode !== 'none' && (
    <Checkbox checked={!!it.photoRequired} onChange={(checked) => updateItem(it.clientKey, { photoRequired: checked })} label="Photo required" className="checklist-editor__item-flag items-center" labelClassName="text-12px" dataRef="page-checklist-editor-item-flag" />
                                        )}
                                        <Checkbox checked={!!it.isRequired} onChange={(checked) => updateItem(it.clientKey, { isRequired: checked })} label="Required item" className="checklist-editor__item-flag items-center" labelClassName="text-12px" dataRef="page-checklist-editor-item-flag" />
                                    </div>
                                </div>

                                {/* Remove button */}
                                <button
                                    type="button"
                                    onClick={() => removeItem(it.clientKey)}
                                    className="inline-flex items-center justify-center w-44px h-44px shrink-0 rounded-4px text-brand-text-muted hover:text-red-600 hover:bg-brand-surface transition-colors"
                                    aria-label="Remove item"
                                    title="Remove item"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}

                    {items.length === 0 && (
                        <div className="text-center py-16px text-12px text-brand-text-muted border border-dashed border-brand-border rounded-md">
                            No items yet. Click "+ Add Item" above.
                        </div>
                    )}
                </div>

                {/* Bottom "Add Item" button — duplicates the header action so
                 * users editing long item lists don't need to scroll back to
                 * the top to append. Hidden when the list is empty since the
                 * empty-state already prompts them to use the top button. */}
                {items.length > 0 && (
                    <div className="mt-12px flex justify-center">
                        <Button variant="secondary" size="sm" onClick={addItem}>
                            + Add Item
                        </Button>
                    </div>
                )}
            </section>

            {/* Footer save row — duplicates header buttons for long forms */}
            <div className="checklist-editor__footer-actions flex flex-wrap items-center justify-end gap-8px">
                <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} isLoading={isSaving}>
                    {isEditing ? 'Save New Version' : 'Create Checklist'}
                </Button>
            </div>
        </PageContainer>
    );
};

export default ChecklistEditor;
